import { getOnDemandStatus } from '@/pages/OrderManagement/util'
import { OrderColumn } from '@components/OrderDispatch/OrderGrid/OrderColumn'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { addCommasToNumber } from '@gravitate-js/excalibrr'
import { getDisplayTimezoneString } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { DispatchWindowFilter } from '../Filters/DispatchWindow'
import {
  FitDateTimeColumn,
  LastSupplyAutoUpdateColumn,
  NoFitColumn,
  RunoutColumn,
  RunoutDateTimeColumn,
  SupplyLockedColumn,
  lpWindowColumnDefs,
} from './DefaultColumnDefs'
import { DeliveryWindowCellRenderer, contractCellRenderer } from './OrderColumnCellrenderers'
import { DeliveryWindowComparator } from './OrderColumnComparators'

export function CarrierWindowStartColumn() {
  return {
    headerName: 'Carrier Window Start',
    field: 'carrier_start',
    valueGetter: (params) => params?.data?.carrier_window_start,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.carrier_window_start), params?.data?.carrier_window_start_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  }
}

export function CarrierWindowEndColumn() {
  return {
    headerName: 'Carrier Window End',
    field: 'carrier_end',
    valueGetter: (params) => params?.data?.carrier_window_end,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.carrier_window_end), params?.data?.carrier_window_end_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  }
}

export const getColumnDefs = (
  setChangeId,
  showModal,
  selectedOrderId,
  scopes,
  setOrderHistoryStoreId,
  setAuditOrderId,
  // OnDemand props
  triggerTPDModal,
  handleTPDDismissModal,
  orderStatus,
  gridAPIRef,
  setIsPopoverVisible,
  isPopoverVisible,
  manualPO
) => [
  {
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    headerName: '',
    field: 'id',
    cellRenderer: 'buttonsCellRenderer',
    filter: false,
    sortable: false,
    menuTabs: [],
    cellRendererParams: { showModal, selectedOrderId, scopes },
    initialFlex: 2.5,
    minWidth: 150,
  },
  OrderColumn(),
  scopes?.is_carrier
    ? {
        headerName: 'Received At',
        cellRenderer: 'carrierRecievedCellRender',
        menuTabs: [],
        initialFlex: 1.5,
        field: 'received_at',
      }
    : {
        headerName: 'Flags',
        field: 'flags.name',
        cellRenderer: 'orderFlagsCellRenderer',
        sortable: false,
        menuTabs: ['filterMenuTab'],
        filterValueGetter: (params) => params?.data?.flags?.map((flag) => flag.name),
        valueGetter: (params) => params?.data?.flags?.map((flag) => flag.name),
        enableRowGroup: true,
        initialFlex: 2.5,
      },
  {
    headerName: 'Driver Status',
    field: 'status',
    cellRenderer: 'dispatchStatusCellRenderer',
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params?.data?.trip_status || params?.data?.state,
    enableRowGroup: true,
    cellRendererParams: { carrierDisplay: !scopes?.is_carrier },
    initialFlex: 4,
    cellClass: 'stringType',
    initialHide: true,
    minWidth: 150,
  },
  {
    headerName: 'Market',
    field: 'market',
    initialFlex: 1,
    initialHide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Sector',
    field: 'sector',
    initialFlex: 1,
    initialHide: true,
    enableRowGroup: true,
  },

  {
    headerName: 'Carrier Status',
    field: 'carrier_updated',
    cellRenderer: 'sentTimeCellRenderer',
    cellRendererParams: {
      setChangeId,
    },
    valueGetter: (params) => params?.data && getOnDemandStatus(params.data),
    comparator: (a, b, nodeA, nodeB, isInverted) => {
      if (!nodeA || !nodeB) {
        return -1
      }
      if (nodeA?.data?.carrier_updated === nodeB?.data?.carrier_updated) {
        return 0
      }
      if (nodeA?.data?.carrier_updated > nodeB?.data?.carrier_updated) {
        return 1
      }
      return -1
    },
    enableRowGroup: true,
    initialFlex: 5,
    cellClass: 'stringType',
    minWidth: 150,
  },
  {
    headerName: 'Carrier',
    field: 'carrier',
    cellRenderer: 'carrierStatusCellRenderer',
    enableRowGroup: true,
    initialFlex: 4,
    cellClass: 'stringType',
    minWidth: 160,
  },
  {
    headerName: 'Destinations',
    autoHeight: true,
    field: 'drops',
    enableRowGroup: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params?.data?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`),
    cellRenderer: 'destinationsCellRenderer',
    cellRendererParams: {
      setOrderHistoryStoreId,
    },
    initialFlex: 4.5,
    minWidth: 150,
  },
  {
    headerName: 'Suppliers',
    field: 'suppliers',
    enableRowGroup: true,
    initialHide: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => {
      const value = params?.data?.suppliers?.map((supplier) => supplier)
      return value?.length > 1 ? value?.filter((supplier, index, array) => array.indexOf(supplier) === index) : value
    },

    initialFlex: 4.5,
  },
  {
    headerName: 'Total Volume',
    field: 'product_ratios',
    enableRowGroup: true,
    initialHide: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => addCommasToNumber(params?.data?.product_ratios?.reduce((a, b) => a + b.volume, 0)),
    initialFlex: 4.5,
  },
  {
    headerName: 'Location Name',
    field: 'location_name',
    valueGetter: (params) => params?.data?.drops?.map((drop) => drop.location_name),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  },
  {
    headerName: 'Site Name',
    field: 'site_name',
    valueGetter: (params) => params?.data?.drops?.map((drop) => drop.store_name),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  },
  {
    headerName: 'Origins',
    autoHeight: true,
    field: 'origins',
    valueGetter: (params) => params?.data?.loads?.map((load) => load.location_name),
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    cellRenderer: 'originsCellRenderer',
    enableRowGroup: true,
    initialFlex: 4,
    minWidth: 150,
  },
  {
    headerName: 'Products',
    field: 'products',
    cellRenderer: 'productsCellRenderer',
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params?.data?.product_ratios?.map((product) => product.name),
    enableRowGroup: true,
    initialFlex: 6,
    minWidth: 220,
  },

  {
    headerName: 'Delivery Window',
    field: 'delivery_window',
    valueGetter: (params) => params?.data?.carrier_window_start,
    comparator: DeliveryWindowComparator,
    cellRenderer: (rowData) => DeliveryWindowCellRenderer(rowData),
    filter: DispatchWindowFilter,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    initialFlex: 8,
    minWidth: 300,
  },
  {
    headerName: 'Load Window',
    field: 'loading_window_start',
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params?.data?.load_window_start,
    cellRenderer: 'loadingWindowCellRenderer',
    filter: 'useLoadingWindowFilter',
    initialFlex: 4,
    comparator: (a, b, nodeA, nodeB, isInverted) => {
      const loadStart1 = nodeA?.data?.loading_window_start
      const loadStart2 = nodeB?.data?.loading_window_start
      const now = new Date().toISOString()
      if (loadStart1 < now && loadStart2 < now) {
        const loadEnd1 = nodeA?.data?.loading_window_end
        const loadEnd2 = nodeB?.data?.loading_window_end
        const orderBool = loadEnd1 > loadEnd2
        return orderBool ? 1 : -1
      }
      return loadStart1 > loadStart2 ? 1 : -1
    },
    minWidth: 150,
  },
  CarrierWindowStartColumn(),
  CarrierWindowEndColumn(),
  {
    headerName: 'Delivery Window Start',
    field: 'dispatch_start',
    valueGetter: (params) => params?.data?.dispatch_window_start,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.dispatch_window_start), params?.data?.dispatch_window_start_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  },
  {
    headerName: 'Delivery Window End',
    field: 'dispatch_end',
    valueGetter: (params) => params?.data?.dispatch_window_end,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.dispatch_window_end), params?.data?.dispatch_window_end_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  },
  ...lpWindowColumnDefs,
  {
    headerName: 'Retain Date',
    field: 'dispatch_window_start',
    valueGetter: (params) =>
      formatters.WEEKDAY_MONTH_DATE_TIME(
        moment(params?.data?.dispatch_window_start),
        params?.data.dispatch_window_start_local
      ),
    comparator: (a, b, nodeA, nodeB) =>
      nodeA?.data?.dispatch_window_start > nodeB?.data?.dispatch_window_start ? 1 : -1,
    initialHide: true,
    initialFlex: 3,
  },
  NoFitColumn(),
  FitDateTimeColumn('date'),
  FitDateTimeColumn('time'),
  RunoutColumn(),
  RunoutDateTimeColumn('date'),
  RunoutDateTimeColumn('time'),
  {
    headerName: 'Purchase Order',
    field: 'po',
    cellRenderer: 'poCellRenderer',
    cellRendererParams: {
      isPopoverVisible,
      setIsPopoverVisible,
      canEdit: scopes?.scopes?.includes('o.w') && !!manualPO,
    },
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    initialFlex: 2,
    cellClass: 'stringType',
  },
  {
    headerName: 'Updated',
    field: 'updated_on',
    cellRenderer: 'updatedTagCellRenderer',
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    cellRendererParams: {
      setAuditOrderId,
    },
    valueGetter: (params) => params?.data?.updated_by,
    comparator: (a, b, nodeA, nodeB, isInverted) => {
      if (!nodeA || !nodeB) {
        return -1
      }
      if (nodeA?.data?.updated_on === nodeB?.data?.updated_on) {
        return 0
      }
      if (nodeA?.data?.updated_on > nodeB?.data?.updated_on) {
        return 1
      }
      return -1
    },
    minWidth: getDisplayTimezoneString() ? 155 : 134,
    cellClass: 'stringType',
  },
  {
    headerName: 'Actions',
    field: 'actions',
    sortable: false,
    cellRenderer: 'carrierActionsCellRenderer',
    cellRendererParams: {
      triggerTPDModal,
      handleTPDDismissModal,
      scopes,
      orderStatus,
      gridAPIRef,
    },
    filter: false,
    // initialFlex: 4,
    minWidth: 200,
    pinned: 'right',
  },
  {
    headerName: 'Est Drop Time',
    field: 'est_drop_time',
    valueGetter: (params) => params?.data?.estimated_end,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.estimated_end), params?.data?.estimated_end_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    minWidth: 220,
  },
  {
    headerName: 'Delivery Date',
    field: 'delivery_date',
    key: 'delivery_date',
    ...defaultDateDef('delivery_date', formatters.DATE, true),
    initialHide: true,
  },
  {
    headerName: 'Delivery Time',
    field: 'delivery_time',
    key: 'delivery_time',
    ...defaultDateDef('delivery_date', formatters.TIME, true),
    filter: false,
    initialHide: true,
    valueGetter: (params) =>
      params?.data?.delivery_date && formatters.TIME(params?.data?.delivery_date, params?.data?.delivery_date_local),
  },

  {
    headerName: 'BOL Date',
    field: 'bol_date',
    ...defaultDateDef('bol_date'),
    initialHide: true,
  },
  {
    headerName: 'Carrier Completed',
    field: 'carrier_completed',
    valueGetter: (params) => params?.data?.carrier_updated,
    valueFormatter: (params) =>
      formatters.DATE_TIME(moment(params?.data?.carrier_updated), params?.data?.carrier_updated_local),
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  },
  {
    headerName: 'Contract',
    field: 'contracts',
    hide: true,
    cellRenderer: contractCellRenderer,
  },
  {
    headerName: 'Driver Notes',
    field: 'driver_note',
    initialHide: true,
    suppressFiltersToolPanel: true,
  },
  {
    headerName: 'Load Number',
    field: 'load_numbers',
    initialHide: true,
    valueGetter: (params) => (params?.data?.load_numbers?.length > 0 ? params.data.load_numbers.map((num) => num) : ''),
    cellClass: 'numberType',
  },
  {
    headerName: 'Customer',
    field: 'customer',
    hide: true,
    valueGetter: (params) => params?.data?.customer || '',
    comparator: (a, b, nodeA, nodeB) =>
      nodeA?.data?.customer?.toLowerCase() > nodeB?.data?.customer?.toLowerCase() ? 1 : -1,
  },
  SupplyLockedColumn(),
  LastSupplyAutoUpdateColumn(),
]
